import { ClockIcon, DropletsIcon, LucideIcon, OctagonXIcon } from "lucide-react";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import { MasterIndex } from "verdi-web-client/Models";
import { Icon } from "@/components/icon";
import { Button } from "@/components/ui/button";
import { SIZE } from "@/configs/sizes";
import { NAMESPACES } from "@/i18n/constants";
import { cn } from "@/utils/class-name";
import { EventDetails } from "./event-details";

import { EventVisualization, type PendingActionDetails } from "./event-visualization";
import { CreateEventForm } from "./forms/create-event-form";
import { DeleteEventForm } from "./forms/delete-event-form";
import { EditEventForm } from "./forms/edit-event-form";
import { TIME_BEFORE_TO_INCLUDE_IN_VISUALIZATION_MS, useOnDemandEventData } from "./use-on-demand-event-data";
import { ManualModeWarning } from "./warnings/manual-mode-warning/manual-mode-warning";
import { useZoneManualDevices } from "./warnings/manual-mode-warning/use-zone-manual-devices";
import { SetEventWarning } from "./warnings/set-event-warning";
import { ZoneSensorReadings } from "./zone-sensor-readings/zone-sensor-readings";

export const FORM_VARIANTS = {
    CREATE_EVENT: "CREATE_EVENT",
    EDIT_EVENT: "EDIT_EVENT",
    DELETE_EVENT: "DELETE_EVENT",
} as const;

type FormVariant = (typeof FORM_VARIANTS)[keyof typeof FORM_VARIANTS];

type ActionButtonConfig = {
    form: FormVariant;
    icon: LucideIcon;
    label: string;
    activeClassName: string;
    requiresEvent: boolean;
};

interface OnDemandIrrigationProps {
    zoneId: string;
}

export default function OnDemandIrrigation({ zoneId }: OnDemandIrrigationProps) {
    const { t } = useTranslation(NAMESPACES.FEATURES.CARDS, { keyPrefix: "zone.on-demand-irrigation" });
    const [currentForm, setCurrentForm] = useState<FormVariant | null>(null);
    const [pendingCreateDuration, setPendingCreateDuration] = useState(0);
    const [pendingCreateStartTime, setPendingCreateStartTime] = useState<Date | null>(null);
    const [pendingEditEndTime, setPendingEditEndTime] = useState<Date | null>(null);
    const manualDevices = useZoneManualDevices(zoneId);

    const { allEvents, visualizationItems, nearestFutureEvent, nearestEventIsSet, nearestFutureEventId, nowDate } =
        useOnDemandEventData(zoneId);

    const ACTION_BUTTONS = useMemo<ActionButtonConfig[]>(
        () => [
            {
                form: FORM_VARIANTS.CREATE_EVENT,
                icon: DropletsIcon,
                label: t("action-buttons.create-event"),
                activeClassName:
                    "bg-primary/50 not-disabled:not-data-[loading]:hover:bg-primary/40 not-disabled:not-data-[loading]:hover:border-primary/40",
                requiresEvent: false,
            },
            {
                form: FORM_VARIANTS.EDIT_EVENT,
                icon: ClockIcon,
                label: t("action-buttons.edit-event"),
                activeClassName:
                    "bg-info/50 not-disabled:not-data-[loading]:hover:bg-info/40 not-disabled:not-data-[loading]:hover:border-info/40",
                requiresEvent: true,
            },
            {
                form: FORM_VARIANTS.DELETE_EVENT,
                icon: OctagonXIcon,
                label: t("action-buttons.delete-event"),
                activeClassName:
                    "bg-destructive/50 not-disabled:not-data-[loading]:hover:bg-destructive/40 not-disabled:not-data-[loading]:hover:border-destructive/40",
                requiresEvent: true,
            },
        ],
        [t],
    );

    const pendingActionDetails = useMemo((): PendingActionDetails | null => {
        if (currentForm === FORM_VARIANTS.CREATE_EVENT && pendingCreateDuration > 0) {
            return {
                type: "create",
                zoneId,
                durationMs: pendingCreateDuration,
                startTime: pendingCreateStartTime ?? undefined,
            };
        }
        if (currentForm === FORM_VARIANTS.EDIT_EVENT && nearestFutureEvent && pendingEditEndTime !== null) {
            return { type: "edit", zoneId, newEndTime: pendingEditEndTime };
        }
        if (currentForm === FORM_VARIANTS.DELETE_EVENT && nearestFutureEvent) {
            return { type: "delete", zoneId };
        }
        return null;
    }, [currentForm, pendingCreateDuration, pendingCreateStartTime, pendingEditEndTime, zoneId, nearestFutureEvent]);

    const handleFormClose = useCallback(() => {
        setCurrentForm(null);
        setPendingCreateDuration(0);
        setPendingCreateStartTime(null);
        setPendingEditEndTime(null);
    }, []);

    const formContainerRef = useRef<HTMLDivElement>(null);
    const prevFormRef = useRef<FormVariant | null>(null);

    // Scroll the form into view when transitioning when a form is opened
    useEffect(() => {
        if (prevFormRef.current === null && currentForm !== null) {
            formContainerRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
        prevFormRef.current = currentForm;
    }, [currentForm]);

    const displayManualModeWarning = manualDevices.length > 0;
    const displaySetEventWarning = nearestFutureEvent && nearestEventIsSet;

    const responseTimeStats = MasterIndex.zone.byID[zoneId]?.responseTimeStats;
    const responseTimeMs = responseTimeStats?.theoreticalP95ActuationDelayMs;

    return (
        <div className={"border border-gray-300 rounded-md p-2 xs:p-4 gap-6 flex flex-col"}>
            <div className={"flex flex-col gap-2"}>
                <h2 className={"text-xl font-bold"}>{t("title")}</h2>
                <EventDetails nearestFutureEvent={nearestFutureEvent} />
                <ZoneSensorReadings zoneId={zoneId} />
            </div>
            <EventVisualization
                nearestFutureEventId={nearestFutureEventId}
                visualizationItems={visualizationItems}
                timeBeforeToInclude={TIME_BEFORE_TO_INCLUDE_IN_VISUALIZATION_MS}
                pendingActionDetails={pendingActionDetails}
                nowDate={nowDate}
            />

            {displaySetEventWarning && (
                <div className={"flex flex-row gap-2 items-center flex-wrap"}>
                    <SetEventWarning
                        nearestFutureEvent={nearestFutureEvent}
                        setEventEndTime={nearestFutureEvent.endTime}
                        setEventStartTime={nearestFutureEvent.startTime}
                        nowDate={nowDate}
                    />
                </div>
            )}
            {displayManualModeWarning && <ManualModeWarning manualDevices={manualDevices} />}
            <div className={"flex w-full flex-row gap-2 items-center justify-between"}>
                {ACTION_BUTTONS.map(({ form, icon, label, activeClassName, requiresEvent }) => (
                    <Button
                        key={form}
                        variant={"secondary"}
                        className={cn("flex-1 p-3", currentForm === form && activeClassName)}
                        disabled={requiresEvent && (!nearestFutureEvent || !nearestFutureEventId || nearestEventIsSet)}
                        onClick={() => {
                            if (currentForm === form) {
                                handleFormClose();
                                return;
                            }
                            setCurrentForm(form);
                            setPendingCreateDuration(0);
                            setPendingCreateStartTime(null);
                            setPendingEditEndTime(null);
                        }}
                    >
                        <div className={"flex flex-row items-center gap-2"}>
                            <Icon icon={icon} size={SIZE.XS} />
                            {label}
                        </div>
                    </Button>
                ))}
            </div>
            <div ref={formContainerRef}>
                {currentForm === FORM_VARIANTS.CREATE_EVENT && (
                    <CreateEventForm
                        onClose={handleFormClose}
                        zoneId={zoneId}
                        manualDevices={manualDevices}
                        existingEvents={allEvents}
                        onPendingDurationChange={setPendingCreateDuration}
                        onPendingStartTimeChange={setPendingCreateStartTime}
                        nowDate={nowDate}
                    />
                )}
                {currentForm === FORM_VARIANTS.EDIT_EVENT && nearestFutureEvent && nearestFutureEventId && (
                    <EditEventForm
                        onClose={handleFormClose}
                        currentEventDuration={nearestFutureEvent.duration}
                        eventEndTime={nearestFutureEvent.endTime}
                        eventStartTime={nearestFutureEvent.startTime}
                        eventId={nearestFutureEventId}
                        zoneId={zoneId}
                        responseTimeMs={responseTimeMs}
                        onPendingNewEndTimeChange={setPendingEditEndTime}
                    />
                )}
                {currentForm === FORM_VARIANTS.DELETE_EVENT && nearestFutureEvent && nearestFutureEventId && (
                    <DeleteEventForm
                        eventId={nearestFutureEventId}
                        zoneId={zoneId}
                        onClose={handleFormClose}
                        eventEndTime={nearestFutureEvent.endTime}
                        eventStartTime={nearestFutureEvent.startTime}
                        responseTimeMs={responseTimeMs}
                    />
                )}
            </div>
        </div>
    );
}
